<template>
  <div v-if="props.results.retrieved_chunks && props.results.retrieved_chunks.length > 0" class="source-files-section">
    <div class="related-documents">{{ TEXT.relatedDocuments }}</div>
    <div class="source-files-grid">
      <Grid
        v-if="gridConfiguration"
        saveConfiguration
        :alias="LIST_NAME"
        :gridConfiguration="gridConfiguration"
        :itemData="props.results.retrieved_documents || []"
        :toolbarOptions="[]"
        :commandClick="onCommandClick"
        @rowClick="handleRowClick"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n-composable'
import useGridConfiguration from '@/composables/useGridConfiguration'
import useFiles from '@/composables/documents/useFiles'
import { TrackerEvents, trackEvent } from '@/plugins/tracker'
import Grid from '@/components/grids/LfGrid/LfGridComponent.vue'
import {
  ActionName,
  CommandClickEventArgs,
  CommandModel,
  commandButtonType
} from '@/components/grids/LfGrid/LfGridTypes'
import { ContextMenuItemModel, RecordClickEventArgs } from '@syncfusion/ej2-vue-grids'
import { Icons } from '@/icons/icons'
import { ListNames } from '@/store/modules/configuration/configurationTypes'
import { useSearchIAStore } from '@/modules/documentSearcher/stores/useSearchIAStore'

// DEFINE PROPS
const props = defineProps<{
  results: any
}>()

// TRANSLATIONS
const { t } = useI18n()

const TEXT = {
  relatedDocuments: t('views.search_ia_result.relatedDocuments') as string,
  openWindow: t('components.context_menu.open_window') as string
}

const ICON = {
  openWindow: Icons.OPEN_WINDOW
}

// STORE
const searchIAStore = useSearchIAStore()

// CONSTANTS
const LIST_NAME = ListNames.RELATED_DOCUMENTS

// COMPUTED
const commands = computed((): CommandModel[] => [
  {
    id: ActionName.OPEN_WINDOW,
    type: commandButtonType.NONE,
    title: TEXT.openWindow,
    buttonOption: {
      iconCss: ICON.openWindow,
      cssClass: 'custombutton'
    }
  }
])

// DATA
const { openInLocation } = useFiles()
const { gridConfiguration } = useGridConfiguration({
  listName: LIST_NAME,
  context: `SearchIA_RelatedDocuments`,
  commandButtons: commands
})

// METHODS
const openDocument = (idDocument: number, idEntity: number, idEntityType: number, documentName: string): void => {
  trackEvent(TrackerEvents.AI_SEARCH_DOCUMENT_OPENED, {
    origin_open_docs_ia_results: 'result_table',
    search_id: searchIAStore.search_id
  })
  const doc = {
    id: idDocument,
    idEntity,
    idEntityType,
    initialFile: documentName
  }
  openInLocation(doc as any)
}

const onCommandClick = (args: CommandClickEventArgs) => {
  const { idDocument, idEntity, idEntityType, documentName } = args.rowData as any
  const commandColumn = args.commandColumn as ContextMenuItemModel

  if (!idDocument || ActionName.OPEN_WINDOW !== commandColumn?.id) return

  openDocument(idDocument, idEntity, idEntityType, documentName)
}

const handleRowClick = (args: RecordClickEventArgs) => {
  const { idDocument, idEntity, idEntityType, documentName } = args.rowData as any
  if (!idDocument) return

  openDocument(idDocument, idEntity, idEntityType, documentName)
}

// Observar cambios en el documento a abrir desde el store
watch(
  () => searchIAStore.documentToOpen,
  (document) => {
    if (document && document.idDocument) {
      trackEvent(TrackerEvents.AI_SEARCH_DOCUMENT_OPENED, {
        origin_open_docs_ia_results: 'reference',
        search_id: searchIAStore.search_id
      })
      openDocument(document.idDocument, document.idEntity, document.idEntityType, document.documentName)
      // Limpiar después de procesar
      searchIAStore.clearDocumentToOpen()
    }
  }
)
</script>

<style lang="scss" scoped>
.source-files-section {
  width: 100%;
}

.related-documents {
  font-family: $inter-font;
  color: $neutral-grey-800;
  margin: $spacing-xxs 0 $spacing-xxs 0;
  padding-bottom: $spacing-xxxs;
  display: flex;
  align-items: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 26px;
}

.source-files-grid {
  width: 100%;
  box-sizing: border-box;

  :deep(.e-control.e-toolbar.e-lib.e-keyboard) {
    display: none;
  }

  :deep(.e-parentmsgbar) {
    display: none;
  }

  :deep(.e-gridpager.e-control.e-pager.e-lib) {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  :deep(.e-gridheader) {
    border-top: none;
  }
}

/* Estilos para los iconos de archivo */
:deep(.e-filemanager) {
  @include file-icons('e-filemanager');
}

/* Ajustes específicos para los iconos en los botones */
:deep(.lf-label-button__text) {
  [class^='e-fe-'] {
    margin-right: $spacing-xs;
    width: 16px;
    height: 16px;
    display: inline-block;
    vertical-align: middle;
  }
}

/* Estilos para los botones de archivo con truncamiento */
.file-button {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  font-family: $inter-font;
  justify-content: flex-start;
  border-radius: 12px;
  text-transform: none;
  background: $neutral-white-000;
  border: 1px solid $neutral-grey-300;
  box-shadow: none;
  min-height: 38px;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
  box-sizing: border-box;
  overflow: hidden;

  :deep(.lf-label-button__content) {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  :deep(.lf-label-button__text) {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: flex;
    align-items: center;
    box-sizing: border-box;

    [class^='e-fe-'] {
      flex-shrink: 0;
      margin-right: $spacing-xs;
      width: 16px;
      height: 16px;
      display: inline-block;
      vertical-align: middle;
    }

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
      flex: 1;
    }
  }
}
</style>

<style lang="scss">
/* Incluir los estilos de iconos de archivo globalmente */
@include file-icons('source-files-grid');

.source-files-grid .e-fe-icon::before {
  width: 21px;
}
</style>
